import {
  addGtag,
  config as gaConfig,
  consentDeniedAll as gaConsentDeniedAll,
  consentGrantedAll as gaConsentGrantedAll,
  event as gaEvent,
  pageview as gaPageview,
  set as gaSet
} from 'vue-gtag'
import {
  Identify,
  Revenue,
  identify as amplitudeIdentify,
  reset as amplitudeReset,
  revenue as amplitudeRevenue,
  setGroup as amplitudeSetGroup,
  setOptOut as amplitudeSetOptOut,
  setUserId as amplitudeSetUserId,
  track as amplitudeTrack
} from '@amplitude/analytics-browser'
import { initAmplitude, isAmplitudeInitialized } from './plugins/amplitude'
import {
  AnalyticsEvent,
  AnalyticsPageView,
  AnalyticsProperties,
  AnalyticsRouteContext,
  AnalyticsValue,
  UserProperties
} from './types/analytics'

export const ANALYTICS_CONSENT_KEY = 'analytics-consent'
export const ANALYTICS_CONSENT_ACCEPTED = 'accepted'
export const ANALYTICS_CONSENT_DECLINED = 'declined'

let googleAnalyticsInitialized = false
let analyticsInitializationPromise: Promise<void> | null = null
let currentUserId: string | null = null
let currentUserProperties: UserProperties = {}

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function isGoogleAnalyticsEnabled(): boolean {
  return process.env.VUE_APP_GA_ENABLED === 'true'
}

function isAmplitudeEnabled(): boolean {
  return process.env.VUE_APP_AMPLITUDE_ENABLED === 'true'
}

function hasPlainObjectValue(value: AnalyticsValue | undefined): value is { [key: string]: AnalyticsValue } {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

type AmplitudeIdentifyValue =
  | string
  | number
  | boolean
  | Array<string | number>
  | { [key: string]: AmplitudeIdentifyValue }
  | Array<{ [key: string]: AmplitudeIdentifyValue }>

function toAmplitudeIdentifyValue(value: AnalyticsValue | undefined): AmplitudeIdentifyValue | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  if (Array.isArray(value)) {
    const arrayValue = value as unknown[]

    if (arrayValue.every((item: unknown) => typeof item === 'string' || typeof item === 'number')) {
      return arrayValue as Array<string | number>
    }

    if (arrayValue.every((item: unknown) => item != null && typeof item === 'object' && !Array.isArray(item))) {
      const normalizedObjects = arrayValue
        .map((item) => toAmplitudeIdentifyValue(item as { [key: string]: AnalyticsValue }))
        .filter((item): item is { [key: string]: AmplitudeIdentifyValue } => {
          return item != null && typeof item === 'object' && !Array.isArray(item)
        })

      return normalizedObjects
    }

    return undefined
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }

  if (hasPlainObjectValue(value)) {
    const normalizedObject: { [key: string]: AmplitudeIdentifyValue } = {}

    for (const [key, nestedValue] of Object.entries(value)) {
      const normalizedValue = toAmplitudeIdentifyValue(nestedValue)
      if (normalizedValue !== undefined) {
        normalizedObject[key] = normalizedValue
      }
    }

    return normalizedObject
  }

  return undefined
}

function normalizeProperties(properties: AnalyticsProperties = {}): AnalyticsProperties {
  const normalized: AnalyticsProperties = {}

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined && value !== null) {
      normalized[key] = value
    }
  }

  return normalized
}

function getCurrentPageLocation(): string | undefined {
  if (!isBrowser()) {
    return undefined
  }

  return window.location.href
}

function getCurrentPagePath(): string | undefined {
  if (!isBrowser()) {
    return undefined
  }

  return window.location.pathname + window.location.search
}

function getCurrentHostname(): string | undefined {
  if (!isBrowser()) {
    return undefined
  }

  return window.location.hostname
}

function getCurrentOrigin(): string | undefined {
  if (!isBrowser()) {
    return undefined
  }

  return window.location.origin
}

function mergeProperties(...propertySets: Array<AnalyticsProperties | undefined>): AnalyticsProperties {
  return normalizeProperties(
    propertySets.reduce<AnalyticsProperties>((acc, properties) => {
      if (!properties) {
        return acc
      }

      return {
        ...acc,
        ...properties
      }
    }, {})
  )
}

function canTrack(): boolean {
  return hasAnalyticsConsent() && (googleAnalyticsInitialized || isAmplitudeInitialized())
}

function applyGoogleAnalyticsUserContext(): void {
  if (!isGoogleAnalyticsEnabled() || !googleAnalyticsInitialized) {
    return
  }

  gaSet({ user_id: currentUserId ?? undefined })
  gaConfig({
    user_properties: normalizeProperties(currentUserProperties)
  })
}

function applyAmplitudeUserContext(): void {
  if (!isAmplitudeEnabled() || !isAmplitudeInitialized()) {
    return
  }

  if (!currentUserId) {
    amplitudeReset()
    return
  }

  amplitudeSetUserId(currentUserId)

  const normalizedUserProperties = normalizeProperties(currentUserProperties)
  if (Object.keys(normalizedUserProperties).length === 0) {
    return
  }

  const identifyEvent = new Identify()
  for (const [key, value] of Object.entries(normalizedUserProperties)) {
    const normalizedValue = toAmplitudeIdentifyValue(value)
    if (normalizedValue !== undefined) {
      identifyEvent.set(key, normalizedValue)
    }
  }

  amplitudeIdentify(identifyEvent)
}

function applyUserContext(): void {
  applyGoogleAnalyticsUserContext()
  applyAmplitudeUserContext()
}

export function getAnalyticsConsentStatus(): string | null {
  if (!isBrowser()) {
    return null
  }

  try {
    return window.localStorage.getItem(ANALYTICS_CONSENT_KEY)
  } catch (error) {
    console.warn('Analytics consent storage read failed', {
      key: ANALYTICS_CONSENT_KEY,
      errorName: error instanceof Error ? error.name : 'UnknownError'
    })
    return null
  }
}

export function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsentStatus() === ANALYTICS_CONSENT_ACCEPTED
}

export function setAnalyticsConsent(status: typeof ANALYTICS_CONSENT_ACCEPTED | typeof ANALYTICS_CONSENT_DECLINED): void {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_KEY, status)
    window.localStorage.setItem(`${ANALYTICS_CONSENT_KEY}-date`, new Date().toISOString())
  } catch (error) {
    console.warn('Analytics consent storage write failed', {
      key: ANALYTICS_CONSENT_KEY,
      errorName: error instanceof Error ? error.name : 'UnknownError'
    })
  }
}

export async function initializeAnalytics(): Promise<void> {
  if (!hasAnalyticsConsent()) {
    return
  }

  if (analyticsInitializationPromise) {
    return analyticsInitializationPromise
  }

  analyticsInitializationPromise = (async () => {
    if (isGoogleAnalyticsEnabled()) {
      if (!googleAnalyticsInitialized) {
        await addGtag()
        googleAnalyticsInitialized = true
      }

      gaConsentGrantedAll('update')
    }

    if (isAmplitudeEnabled()) {
      if (!isAmplitudeInitialized()) {
        initAmplitude()
      }

      if (isAmplitudeInitialized()) {
        amplitudeSetOptOut(false)
      }
    }

    applyUserContext()
  })().finally(() => {
    analyticsInitializationPromise = null
  })

  return analyticsInitializationPromise
}

export function disableAnalytics(): void {
  if (isGoogleAnalyticsEnabled() && googleAnalyticsInitialized) {
    gaConsentDeniedAll('update')
  }

  if (isAmplitudeEnabled() && isAmplitudeInitialized()) {
    amplitudeSetOptOut(true)
    amplitudeReset()
  }
}

export async function acceptAnalyticsConsent(): Promise<void> {
  setAnalyticsConsent(ANALYTICS_CONSENT_ACCEPTED)
  await initializeAnalytics()
}

export function declineAnalyticsConsent(): void {
  setAnalyticsConsent(ANALYTICS_CONSENT_DECLINED)
  disableAnalytics()
}

export async function syncAnalyticsUser(userId: string | null, properties: UserProperties = {}): Promise<void> {
  currentUserId = userId
  currentUserProperties = normalizeProperties(properties)

  if (!hasAnalyticsConsent()) {
    return
  }

  await initializeAnalytics()
  applyUserContext()
}

export async function clearAnalyticsUser(): Promise<void> {
  currentUserId = null
  currentUserProperties = {}

  if (!hasAnalyticsConsent()) {
    return
  }

  await initializeAnalytics()
  applyUserContext()
}

export async function setUserProperties(properties: UserProperties): Promise<void> {
  currentUserProperties = normalizeProperties(properties)

  if (!hasAnalyticsConsent()) {
    return
  }

  await initializeAnalytics()
  applyUserContext()
}

export async function setAnalyticsUserId(userId: string): Promise<void> {
  await syncAnalyticsUser(userId, currentUserProperties)
}

export async function trackEvent(
  eventData: AnalyticsEvent,
  routeContext?: AnalyticsRouteContext
): Promise<void> {
  if (!hasAnalyticsConsent()) {
    return
  }

  await initializeAnalytics()

  if (!canTrack()) {
    return
  }

  const { name, category, label, value, customParameters } = eventData
  const eventProperties = mergeProperties(routeContext, customParameters)

  if (isGoogleAnalyticsEnabled() && googleAnalyticsInitialized) {
    gaEvent(name, {
      event_category: category || 'general',
      event_label: label,
      value,
      ...eventProperties
    })
  }

  if (isAmplitudeEnabled() && isAmplitudeInitialized()) {
    amplitudeTrack(name, mergeProperties(
      {
        event_category: category || 'general',
        event_label: label,
        value
      },
      eventProperties
    ))
  }
}

export async function trackPageView(pageView: AnalyticsPageView): Promise<void> {
  if (!hasAnalyticsConsent()) {
    return
  }

  await initializeAnalytics()

  if (!canTrack()) {
    return
  }

  const normalizedPageView = mergeProperties(
    {
      hostname: getCurrentHostname(),
      origin: getCurrentOrigin(),
      page_path: pageView.page_path ?? getCurrentPagePath(),
      page_location: pageView.page_location ?? getCurrentPageLocation(),
      page_title: pageView.page_title
    },
    pageView
  )

  if (isGoogleAnalyticsEnabled() && googleAnalyticsInitialized) {
    gaPageview(normalizedPageView)
  }

  if (isAmplitudeEnabled() && isAmplitudeInitialized()) {
    amplitudeTrack('page_view', normalizedPageView)
  }
}

export async function trackUserAction(
  action: string,
  element: string,
  additionalData?: AnalyticsProperties
): Promise<void> {
  await trackEvent({
    name: action,
    category: 'user_interaction',
    label: element,
    customParameters: mergeProperties(
      {
        interaction_type: action,
        element_type: element
      },
      additionalData
    )
  })
}

export async function trackFeatureUsage(
  feature: string,
  action: string,
  details?: AnalyticsProperties,
  routeContext?: AnalyticsRouteContext
): Promise<void> {
  await trackEvent(
    {
      name: 'feature_usage',
      category: feature,
      label: action,
      customParameters: mergeProperties(
        {
          feature_name: feature,
          feature_action: action
        },
        details
      )
    },
    routeContext
  )
}

export async function trackError(
  error: Error,
  context: string,
  fatal = false,
  details?: AnalyticsProperties
): Promise<void> {
  if (!hasAnalyticsConsent()) {
    return
  }

  await initializeAnalytics()

  if (!canTrack()) {
    return
  }

  const errorProperties = mergeProperties(
    {
      error_context: context,
      stack_trace: error.stack?.substring(0, 500)
    },
    details
  )

  if (isGoogleAnalyticsEnabled() && googleAnalyticsInitialized) {
    gaEvent('exception', {
      description: `${context}: ${error.message}`,
      fatal,
      ...errorProperties
    })
  }

  if (isAmplitudeEnabled() && isAmplitudeInitialized()) {
    amplitudeTrack('error', mergeProperties(
      {
        error_message: error.message,
        fatal
      },
      errorProperties
    ))
  }
}

export async function trackRevenue(
  productId: string,
  price: number,
  quantity: number,
  revenueType?: string
): Promise<void> {
  if (!hasAnalyticsConsent() || !isAmplitudeEnabled()) {
    return
  }

  await initializeAnalytics()

  if (!isAmplitudeInitialized()) {
    return
  }

  const revenueEvent = new Revenue()
    .setProductId(productId)
    .setPrice(price)
    .setQuantity(quantity)

  if (revenueType) {
    revenueEvent.setRevenueType(revenueType)
  }

  amplitudeRevenue(revenueEvent)
}

export async function setUserGroup(groupType: string, groupName: string | string[]): Promise<void> {
  if (!hasAnalyticsConsent() || !isAmplitudeEnabled()) {
    return
  }

  await initializeAnalytics()

  if (!isAmplitudeInitialized()) {
    return
  }

  amplitudeSetGroup(groupType, groupName)
}

export async function setAmplitudeOptOut(optOut: boolean): Promise<void> {
  if (!isAmplitudeEnabled()) {
    return
  }

  if (optOut) {
    disableAnalytics()
    return
  }

  await initializeAnalytics()
}

export async function resetAmplitude(): Promise<void> {
  await clearAnalyticsUser()
}

export function normalizeActionDetails(
  value?: AnalyticsValue | AnalyticsProperties
): AnalyticsProperties {
  if (value === undefined) {
    return {}
  }

  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return normalizeProperties(value)
  }

  return {
    button_value: value
  }
}
